<template>
  <div v-if="itemData || isLoading" class="modal-overlay">
    <div class="detail-container" @click="handleModalClick" @click.stop ref="modalRef">
      <div class="modal-header">
        <div class="header-left">
          <h1 class="detail-title">Item Detail</h1>
          <div class="language-select-wrapper" ref="languageDropdownRef" @click.stop>
            <div class="custom-select" :class="{ 'is-open': showDropdown }" @click="toggleDropdown">
              <span>{{ currentLanguage === 'en' ? 'English' : '中文' }}</span>
              <img :src="dropdownIcon" alt="dropdown" class="select-arrow" />
            </div>
            <div v-if="showDropdown" class="custom-dropdown">
              <div
                class="dropdown-item"
                :class="{ selected: currentLanguage === 'en' }"
                @click="selectLanguage('en')"
              >
                English
              </div>
              <div
                class="dropdown-item"
                :class="{ selected: currentLanguage === 'zh' }"
                @click="selectLanguage('zh')"
              >
                中文
              </div>
            </div>
          </div>
        </div>
        <button class="close-btn" @click="handleClose">
          <img :src="closeIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div class="modal-body">
        <div class="tabs-wrapper">
          <div class="tabs">
            <button
              :class="['tab-btn', { active: activeTab === 'information' }]"
              @click="activeTab = 'information'"
            >
              Information
            </button>
            <button
              :class="['tab-btn', { active: activeTab === 'value' }]"
              @click="activeTab = 'value'"
            >
              Value
            </button>
          </div>
          <div class="tabs-spacing"></div>
        </div>

        <div class="content-wrap" v-if="itemData">
          <div v-if="activeTab === 'information'" class="detail-body">
            <div class="info-row info-row--type">
              <span class="info-label">Type:</span>
              <div class="item-tags">
                <span v-if="itemData.type?.toLowerCase() === 'routine'" class="tag tag-routine">Routine</span>
                <span v-else-if="itemData.type?.toLowerCase() === 'baseline'" class="tag tag-baseline">Baseline</span>
                <span v-else class="info-value">{{ itemData.type_text }}</span>
              </div>
            </div>

            <div class="info-row">
              <span class="info-label">ID:</span>
              <span class="info-value">{{ itemData.item_id }}</span>
            </div>

            <div class="info-row">
              <span class="info-label">Name:</span>
              <span class="info-value">{{
                currentLanguage === 'en'
                  ? itemData.item_name
                  : itemData.item_name_zh || itemData.item_name
              }}</span>
            </div>

            <div class="info-row">
              <span class="info-label">Photo:</span>
              <div class="info-photo">
                <img
                  v-if="itemData.icon_url"
                  :src="getIconUrl(itemData.icon_url)"
                  alt="item icon"
                  class="photo-img"
                  @error="handleImageError"
                />
                <div v-else class="photo-placeholder">
                  <img :src="placeholderIcon" alt="placeholder" />
                </div>
              </div>
            </div>

            <div class="info-row">
              <span class="info-label">Duration:</span>
              <span class="info-value">{{ itemData.duration_text }}</span>
            </div>

            <div class="info-row info-row--tags">
              <span class="info-label">Seasons:</span>
              <div class="info-seasons">
                <span v-for="season in displaySeasons" :key="season" class="season-tag">
                  {{ season }}
                </span>
              </div>
            </div>

            <div class="info-row">
              <span class="info-label">Section:</span>
              <span class="info-value">{{ itemData.section }}</span>
            </div>

            <div class="info-row">
              <span class="info-label">Location:</span>
              <span class="info-value">{{ displayLocations }}</span>
            </div>

            <div class="info-row">
              <span class="info-label">Proof Needed:</span>
              <span class="info-value">{{ itemData.photo_required === 1 ? 'Yes' : 'No' }}</span>
            </div>

            <div v-if="itemData.type?.toLowerCase() === 'routine'" class="info-row">
              <span class="info-label">Follow-up Needed:</span>
              <span class="info-value">{{ itemData.follow_up_needed === 1 ? 'Yes' : 'No' }}</span>
            </div>

            <div v-if="itemData.type?.toLowerCase() === 'routine'" class="info-row">
              <span class="info-label">Plan Includes:</span>
              <span class="info-value">{{ getPlanIncludesText() }}</span>
            </div>

            <div class="info-row">
              <span class="info-label">Frequency:</span>
              <span class="info-value">{{ getFrequencyText() }}</span>
            </div>

            <div class="info-row">
              <span class="info-label">Status:</span>
              <span class="info-value status-text" :class="{ inactive: itemData.status !== 1 }">
                {{ itemData.status_text }}
              </span>
            </div>

            <div class="info-row description-row">
              <span class="info-label">Description:</span>
              <div class="info-description">
                <p>
                  {{
                    currentLanguage === 'en'
                      ? itemData.description
                      : itemData.description_zh || itemData.description
                  }}
                </p>
              </div>
            </div>
          </div>

          <div
            v-else-if="activeTab === 'value' && itemData.type?.toLowerCase() === 'baseline'"
            class="detail-body"
          >
            <template v-if="itemData.checklist_values?.components?.length">
              <div
                v-for="(option, index) in itemData.checklist_values.components"
                :key="index"
                class="value-option"
              >
                <h3 class="option-title">#{{ index + 1 }} {{ option.component }}</h3>
                <div v-if="hasVisibleOptionValues(option.values)" class="option-values">
                  {{ option.values.join(', ') }}
                </div>
                <div class="option-type">
                  {{ option.field_type_text }}
                </div>
              </div>
            </template>
            <div v-else class="empty-state">
              <p>No values configured for this baseline item</p>
            </div>
          </div>

          <div
            v-else-if="activeTab === 'value' && itemData.type?.toLowerCase() === 'routine'"
            class="detail-body routine-values"
          >
            <div v-if="itemData.values" class="routine-values-content">
              <div v-for="(values, key) in itemData.values" :key="key" class="routine-value-item">
                <h3 class="routine-value-title">
                  {{ key.charAt(0).toUpperCase() + key.slice(1) }}
                </h3>
                <p v-if="values?.length" class="routine-value-content">
                  {{ values.join(', ') }}
                </p>
                <p v-else class="routine-value-content" style="color: #999">No value</p>
              </div>
            </div>
            <div v-else class="empty-state">
              <p>No values configured for this routine item</p>
            </div>
          </div>
        </div>
        <!-- 接口异常空状态 -->
        <div v-else-if="error" class="empty-state">
          <p>{{ error }}</p>
        </div>
        <!-- 加载中展示Loading组件 -->
        <div v-else-if="isLoading" class="empty-state loading-state">
          <LoadingIndicator />
        </div>
      </div>

      <div class="detail-actions" v-if="itemData">
        <button v-if="itemData.status === 1" class="action-btn" @click="handleDeactivate">
          <span class="action-icon-wrap" aria-hidden="true">
            <img :src="deactivateIcon" alt="" class="action-icon-img action-icon-default" />
            <img :src="deactivateWhiteIcon" alt="" class="action-icon-img action-icon-hover" />
          </span>
          Deactivate
        </button>
        <button v-else class="action-btn action-btn--reactivate" @click="handleReactivate">
          <span class="action-icon-wrap" aria-hidden="true">
            <img :src="activateIcon" alt="" class="action-icon-img action-icon-default" />
            <img :src="activateWhiteIcon" alt="" class="action-icon-img action-icon-hover" />
          </span>
          Reactivate
        </button>
        <button class="action-btn" @click="handleDelete">
          <span class="action-icon-wrap" aria-hidden="true">
            <img :src="deleteIcon" alt="" class="action-icon-img action-icon-default" />
            <img :src="deleteWhiteIcon" alt="" class="action-icon-img action-icon-hover" />
          </span>
          Delete
        </button>
        <button class="action-btn" @click="handleEdit">
          <span class="action-icon-wrap" aria-hidden="true">
            <img :src="editIcon" alt="" class="action-icon-img action-icon-default" />
            <img :src="editWhiteIcon" alt="" class="action-icon-img action-icon-hover" />
          </span>
          Edit
        </button>
      </div>
    </div>

    <!-- Sub Modals - placed inside the main modal overlay, outside detail-container -->
    <EditItem
      v-if="showEditModal"
      :item-id="String(props.itemId)"
      @close="closeEditModal"
      @saved="handleEditSaved"
      @toast="forwardToast"
    />

    <DeleteCheck
      v-if="showDeleteModal"
      :item-id="String(props.itemId)"
      :item-name="itemName"
      @close="closeDeleteModal"
      @deleted="handleDeleteDeleted"
      @toast="forwardToast"
    />

    <CheckActive
      v-if="showCheckActiveModal"
      :item-id="String(props.itemId)"
      :action="checkActiveAction"
      :item-name="itemName"
      @close="closeCheckActiveModal"
      @saved="handleCheckActiveSaved"
      @toast="forwardToast"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted, onMounted, computed } from 'vue'
import { getChecklistDetail, type ChecklistDetailData } from '@/api/itemdetail'
import { formatSeasonDisplayLabels } from '@/utils/seasons'
import { resolveLocationsForItem } from '@/utils/checklistLocations'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import EditItem from './EditItem.vue'
import DeleteCheck from './DeleteCheck.vue'
import CheckActive from './CheckActive.vue'
import '@/styles/checklist-item-tags.css'

// 图标导入
import dropdownIcon from '@/assets/downarrow.png'
import closeIcon from '@/assets/Fill 1@2x.png'
import placeholderIcon from '@/assets/photo.png'
import deactivateIcon from '@/assets/Schedule.png'
import activateIcon from '@/assets/activate.png'
import deleteIcon from '@/assets/delete(1).png'
import editIcon from '@/assets/edit.png'
import deactivateWhiteIcon from '@/assets/deactivate_white@2x (1).png'
import activateWhiteIcon from '@/assets/activate_white@2x.png'
import deleteWhiteIcon from '@/assets/delete_white@2x.png'
import editWhiteIcon from '@/assets/edit_white@2x.png'

interface Props {
  itemId: string | number
}
const props = defineProps<Props>()
const emit = defineEmits<{
  close: []
  updated: [itemId: string]
  deleted: [itemId: string]
  toast: [data: { type: 'success' | 'warning' | 'error'; message: string }]
}>()

const activeTab = ref<'information' | 'value'>('information')
const currentLanguage = ref<'en' | 'zh'>('en')
const showDropdown = ref(false)
const error = ref('')
const isLoading = ref(false) // 加载状态变量
const itemData = ref<ChecklistDetailData | null>(null) // 后端数据存储，初始为null

// DOM引用
const languageDropdownRef = ref<HTMLDivElement | null>(null)
const modalRef = ref<HTMLDivElement | null>(null)

// Sub modal states
const showEditModal = ref(false)
const showDeleteModal = ref(false)
const showCheckActiveModal = ref(false)
const checkActiveAction = ref<'deactivate' | 'reactivate'>('deactivate')

// Get item name for modals
const itemName = computed(() => {
  if (!itemData.value) return ''
  return currentLanguage.value === 'en'
    ? itemData.value.item_name
    : itemData.value.item_name_zh || itemData.value.item_name
})

const displaySeasons = computed(() => {
  if (!itemData.value) return []
  if (currentLanguage.value === 'en') {
    return formatSeasonDisplayLabels(itemData.value.seasons)
  }
  return itemData.value.seasons_zh?.length
    ? itemData.value.seasons_zh
    : formatSeasonDisplayLabels(itemData.value.seasons)
})

const displayLocations = computed(() => {
  if (!itemData.value) return ''
  return resolveLocationsForItem(itemData.value).join(', ')
})

function toggleDropdown(): void {
  showDropdown.value = !showDropdown.value
}

function selectLanguage(lang: 'en' | 'zh'): void {
  currentLanguage.value = lang
  showDropdown.value = false
  fetchDetail() // 切换语种重新请求后端数据
}

function handleModalClick(event: MouseEvent): void {
  if (languageDropdownRef.value?.contains(event.target as Node)) return
  showDropdown.value = false
}

function getIconUrl(icon: string): string {
  if (!icon) return ''
  if (icon.startsWith('http://') || icon.startsWith('https://')) return icon
  const baseUrl = 'https://hm.back.escroud.com'
  return icon.startsWith('/') ? `${baseUrl}${icon}` : `${baseUrl}/${icon}`
}

function handleImageError(event: Event): void {
  const img = event.target as HTMLImageElement
  img.style.display = 'none'
}

function getFrequencyText(): string {
  if (!itemData.value) return ''
  const raw =
    currentLanguage.value === 'en'
      ? itemData.value.frequency
      : itemData.value.frequency_zh || itemData.value.frequency
  if (!raw) return ''
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

function hasVisibleOptionValues(values?: string[]): boolean {
  return (values ?? []).some((value) => String(value).trim().length > 0)
}

function getPlanIncludesText(): string {
  if (!itemData.value?.follow_up_care_plans) return ''
  const plans = itemData.value.follow_up_care_plans
  if (!plans.length) return ''
  const planMap: Record<string, string> = {
    essential_care: 'Essential',
    enhanced_care: 'Enhanced',
    premium_care: 'Premium',
  }
  return plans.map((plan) => planMap[plan] || plan).join(', ')
}

// 请求后端接口获取详情
async function fetchDetail(): Promise<void> {
  error.value = ''
  isLoading.value = true // 开始请求，开启loading
  try {
    const rawId = props.itemId
    const parseId = Number(typeof rawId === 'string' ? rawId.trim() : rawId)
    if (isNaN(parseId)) {
      error.value = 'Item ID invalid'
      return
    }
    const res = await getChecklistDetail({ id: parseId })
    if (res.code === 200) {
      itemData.value = res.data
    } else {
      error.value = res.msg || 'Get detail failed'
    }
  } catch (err) {
    console.error('详情接口异常', err)
    error.value = 'Network request failed'
  } finally {
    isLoading.value = false // 请求结束，关闭loading
  }
}

// 组件挂载自动请求后端数据
onMounted(() => {
  fetchDetail()
})

// 事件回调函数
function forwardToast(data: { type: 'success' | 'warning' | 'error'; message: string }): void {
  emit('toast', data)
}

function handleClose(): void {
  emit('close')
}

function handleEdit(): void {
  showEditModal.value = true
}

function closeEditModal(): void {
  showEditModal.value = false
}

function handleEditSaved(): void {
  showEditModal.value = false
  void fetchDetail()
  emit('updated', String(props.itemId))
}

function handleDeactivate(): void {
  checkActiveAction.value = 'deactivate'
  showCheckActiveModal.value = true
}

function closeCheckActiveModal(): void {
  showCheckActiveModal.value = false
}

function handleCheckActiveSaved(): void {
  showCheckActiveModal.value = false
  void fetchDetail()
  emit('updated', String(props.itemId))
}

function handleReactivate(): void {
  checkActiveAction.value = 'reactivate'
  showCheckActiveModal.value = true
}

function handleDelete(): void {
  showDeleteModal.value = true
}

function closeDeleteModal(): void {
  showDeleteModal.value = false
}

function handleDeleteDeleted(success: boolean): void {
  showDeleteModal.value = false
  if (success) {
    emit('deleted', String(props.itemId))
  }
}

onUnmounted(() => {
  // 清理工作
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 24px 24px 45px;
  z-index: 10000;
  box-sizing: border-box;
}

.detail-container {
  background-color: #ffffff;
  border-radius: 12px;
  width: 540px;
  max-width: calc(100% - 48px);
  height: calc(100vh - 69px);
  max-height: calc(100vh - 69px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 24px;
  flex-shrink: 0;
  height: auto;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.detail-title {
  font-size: 16px;
  font-weight: 600;
  font-family:
    'SF Pro',
    SFPro,
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 24px 0 16px;
  text-align: left;
}

.language-select-wrapper {
  position: relative;
  width: 110px;
  height: 24px;
  flex-shrink: 0;
}

.language-select-wrapper .custom-select {
  position: relative;
  height: 24px;
  border-radius: 16px;
  padding: 0 24px 0 12px;
  border: 1px solid #d9d9d9;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s;
  user-select: none;
  display: flex;
  align-items: center;
  color: #000000;
  box-sizing: border-box;
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.language-select-wrapper .custom-select.is-open {
  border-color: #666666;
}

.language-select-wrapper .select-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  pointer-events: none;
  transition: transform 0.2s ease;
}

.language-select-wrapper .custom-select.is-open .select-arrow {
  transform: translateY(-50%) rotate(180deg);
}

.language-select-wrapper .custom-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
}

.language-select-wrapper .dropdown-item {
  padding: 8px 16px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  cursor: pointer;
  transition: background 0.2s;
}

.language-select-wrapper .dropdown-item:hover {
  background: #f5f5f5;
}

.language-select-wrapper .custom-dropdown .dropdown-item.selected {
  font-weight: 600;
}

.close-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  margin-top: 24px;
}

.modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  scrollbar-gutter: stable;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}
.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}
.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.tabs-wrapper {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #ffffff;
  padding-bottom: 0;
}

.tabs {
  display: flex;
  background-color: #f5f6fb;
  border-radius: 16px;
  padding: 0;
  margin-top: 0;
  height: 32px;
}

.tabs-spacing {
  height: 16px;
  flex-shrink: 0;
}

.content-wrap {
  padding-bottom: 0;
}

.tab-btn {
  flex: 1;
  height: 32px;
  padding: 0 20px;
  border: none;
  border-radius: 16px;
  background-color: #f5f6fb;
  color: #999999;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn.active {
  background-color: #000000;
  color: #ffffff;
}

.tab-btn:hover:not(.active) {
  color: #999999;
}

.detail-body {
  margin-top: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.info-label {
  width: 160px;
  text-align: right;
  font-size: 0.9rem;
  color: #000000;
  font-weight: 500;
  flex-shrink: 0;
}

.info-value {
  font-size: 14px;
  color: #000000;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  text-align: left;
  white-space: normal;
  word-break: break-word;
}

.info-photo {
  display: flex;
  align-items: center;
}
.photo-img {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: contain;
}
.photo-placeholder {
  width: 60px;
  height: 60px;
  background: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.photo-placeholder img {
  width: 30px;
  height: 30px;
  opacity: 0.4;
}

.info-row--type {
  align-items: center;
}

.info-row--tags {
  align-items: flex-start;
}

.status-text {
  color: #00aeef;
  font-weight: 600;
}
.status-text.inactive {
  color: #fb0101;
}

.description-row {
  align-items: flex-start;
  border-top: 1px solid #e0e0e0;
  padding-top: 16px;
  margin-top: 0;
}
.info-description {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #000;
}
.info-description p {
  margin: 0;
}

.value-option {
  padding: 0;
  background: #f9f9f9;
  border-radius: 8px;
}
.option-title {
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0 0 16px 0;
  word-break: break-all;
  overflow-wrap: break-word;
}
.option-values {
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  text-align: left;
  margin: 0 0 16px 0;
  padding-left: 25px;
  word-break: break-all;
  overflow-wrap: break-word;
}
.option-type {
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  padding-left: 25px;
}

.routine-value-item {
  margin-bottom: 12px;
}
.routine-value-title {
  font-size: 14px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  text-align: left;
  margin: 0 0 4px 0;
}
.routine-value-content {
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  text-align: left;
  padding-left: 0;
  word-break: break-all;
  overflow-wrap: break-word;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
  color: #999;
}

.empty-state.loading-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
  min-height: 0;
}

.detail-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px 24px;
  background-color: #ffffff;
  flex-shrink: 0;
  margin-top: 0;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 16px;
  height: 28px;
  border: 1px solid #000000;
  background: #fff;
  border-radius: 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  line-height: 28px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease;
}

.action-btn:hover {
  background: #000000;
  border-color: #000000;
  color: #ffffff;
}

.action-icon-wrap {
  position: relative;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.action-icon-img {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.action-icon-wrap .action-icon-default,
.action-icon-wrap .action-icon-hover {
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 0.2s ease;
}

.action-icon-wrap .action-icon-hover {
  opacity: 0;
}

.action-btn:hover .action-icon-wrap .action-icon-default {
  opacity: 0;
}

.action-btn:hover .action-icon-wrap .action-icon-hover {
  opacity: 1;
}

@media (max-width: 768px) {
  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  .info-label {
    width: 100%;
    text-align: left;
  }
  .detail-actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}
</style>
